import { Color, Mesh, Vector2, Vector3 } from 'three';
import type { ShaderMaterial } from 'three';
import { GAME_NAME, HAZARDS, MAPS, MAP_ORDER, TUNING, WORLD_VERSION } from '@cloudpearl/config';
import type { MapDef, MapId, QualityPreset } from '@cloudpearl/config';
import { InputRecorder, Sim, autopilot, dailySeedCode, normalizeSeedCode, randomSeedCode, seedFromCode } from '@cloudpearl/core';
import type { SimEvent } from '@cloudpearl/core';
import { PEARL_RADIUS, SharedUniforms, beamGeometry, createBeamMaterial, createWorldTextures } from '@cloudpearl/assets';
import type { MaterialTextures } from '@cloudpearl/assets';
import { createAudioEngine } from '@cloudpearl/audio';
import type { AudioEngine } from '@cloudpearl/audio';
import { Renderer } from '../render/Renderer';
import type { PostParams } from '../render/Renderer';
import { World } from '../world/World';
import { EnvLighting } from '../world/EnvLighting';
import { PlayerView } from '../player/PlayerView';
import { GhostTrails } from '../world/GhostTrails';
import { FAR_LAYER } from '../world/Terrain';
import { ChaseCamera } from '../camera/ChaseCamera';
import { Input } from '../input/Input';
import { Ui } from '../ui/Ui';
import { formatRoomCode } from '../ui/Ui';
import type { RunMode, SettingsState } from '../ui/Ui';
import { Session } from '../net/Session';
import { RemotePearl } from '../net/RemotePearl';
import type { NetState } from '../net/RemotePearl';
import { normalizeRoomCode } from '../net/Room';
import { storage } from './storage';
import { presetFor } from './quality';

type State = 'boot' | 'idle' | 'playing' | 'paused' | 'crashed' | 'busy';

const SIM_DT = 1 / TUNING.tickRate;
const _ahead = new Vector3();
const _probe = new Vector3();
const _proj = new Vector3();
const _mid = new Vector3();
/** Shared runs: host starts on the left, guest on the right. */
const SPAWN_X = 1.6;
/** The friend's pearl until they tell us their colour. */
const FRIEND_TINT = '#bfd6ff';
const REVIVE_DELAY = 2.5;

/**
 * Orchestrates the run: fixed-step deterministic sim (120 Hz) + interpolated rendering, the
 * idle → run → crash → retry loop, and wiring between sim events, audio, particles and UI.
 */
export class Game {
  private readonly container: HTMLElement;
  private readonly surface: HTMLElement;
  private readonly shared = new SharedUniforms();
  private readonly renderer: Renderer;
  private readonly textures: MaterialTextures;
  private readonly cam: ChaseCamera;
  private readonly env: EnvLighting;
  private readonly input: Input;
  private readonly ui: Ui;
  private readonly audio: AudioEngine;
  private quality: QualityPreset;
  private settings: SettingsState;

  private map: MapDef;
  private seedCode: string;
  private isDaily: boolean;
  private beat: number | null = null;
  private sim!: Sim;
  private world: World | null = null;
  private player: PlayerView | null = null;

  private state: State = 'boot';
  private acc = 0;
  private time = 0;
  private last = 0;
  /** Display refresh interval (ms), tracked from raw rAF deltas (fast to drop, slow to rise). */
  private refreshMs = 1000 / 60;
  private rafPrev = 0;
  private timeScale = 1;
  private realDt = 1 / 60;
  private crashT = 0;
  private crashShown = false;
  private best: number | null = null;
  private passedBest = false;
  private readonly recorder = new InputRecorder();
  private pendingStart = false;
  private tiltAsked = false;
  private fadeTarget = 1;
  private readonly post: PostParams = { speed: 0, hero: new Vector3(0.5, 0.3, 0.05), focus: new Vector2(0.5, 0.55), storm: 0, flash: 0, flashColor: new Color(1, 1, 1), fade: 0, desat: 0 };
  private speedFx = 0;
  /** ?autopilot — the hare plays itself (demos, testing, balancing). */
  private readonly autopilot = new URLSearchParams(location.search).has('autopilot');
  private readonly autoInput = { hold: false, steer: 0 };
  private gateCombo = 0;
  private lastGateT = -100;
  private debugEl: HTMLElement | null = null;
  /** Last map load breakdown (ms) — exposed for profiling. */
  loadTimings: Record<string, number> = {};
  private debugAcc = { t: 0, frames: 0, worst: 0 };

  // Shared runs (PeerJS).
  private mp: Session | null = null;
  private remote: RemotePearl | null = null;
  private remoteView: PlayerView | null = null;
  private remoteWasAlive = true;
  private remoteWasGrounded = true;
  private bumpCool = 0;
  private reviveAt = 0;
  private spectating = false;
  private resultShown = false;
  private countTimers: number[] = [];
  /** A world the friend switched to while we were busy — applied once we're back on the menu. */
  private pendingAdopt: { map: MapId; seed: string } | null = null;
  private readonly joinCode: string | null;
  private friendColor = FRIEND_TINT;
  /** A beam of light over the friend's pearl, so you can always find them. */
  private friendBeam: Mesh | null = null;
  /** Solo: every earlier run on this world, as faint trails. */
  private ghosts: GhostTrails | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.settings = storage.settings();
    this.quality = presetFor(this.settings.quality);

    this.surface = document.createElement('div');
    this.surface.className = 'surface';
    const canvas = document.createElement('canvas');
    canvas.className = 'view';
    this.surface.appendChild(canvas);
    container.appendChild(this.surface);

    this.renderer = new Renderer(canvas, this.quality, this.shared);
    this.textures = createWorldTextures();
    this.env = new EnvLighting(this.renderer.gl);
    this.cam = new ChaseCamera(1);
    this.cam.camera.layers.enable(FAR_LAYER);
    this.input = new Input(this.surface);
    this.audio = createAudioEngine();
    this.audio.setMuted(!this.settings.sound);
    this.audio.setVolumes({ music: this.settings.music ? 0.45 : 0 });

    this.ui = new Ui(
      container,
      {
        // The Run button lives in the overlay, not on the play surface: unlock audio/tilt here too.
        onStart: () => {
          this.action();
          this.gestureUnlock();
        },
        onMap: (dir) => void this.switchMap(dir),
        onNewSeed: () => void this.setSeed(randomSeedCode(Math.random()), false),
        onDaily: () => void this.setSeed(dailySeedCode(new Date()), true),
        onSeedEntered: (code) => void this.setSeed(code, false),
        onCopyLink: (kind) => void this.copyLink(kind),
        onSettings: (s) => void this.applySettings(s),
        onPause: () => this.togglePause(),
        onResume: () => this.togglePause(false),
        onMenu: () => this.toMenu(),
        onRetry: () => this.retry(),
        onHost: () => this.openRoom(),
        onJoin: (code) => this.openRoom(code),
        onLeave: () => this.leaveRoom('You left the room'),
        onMode: (mode) => this.setMode(mode),
        sfx: (kind) => this.audio.play({ type: kind }),
      },
      this.settings,
    );

    // Initial map/seed from the URL (shared links), else last map + today's seed.
    const params = new URLSearchParams(location.search);
    const urlMap = params.get('map');
    const mapId: MapId = urlMap === 'sky' || urlMap === 'dunes' ? urlMap : (storage.lastMap() ?? MAP_ORDER[0]!);
    this.map = MAPS[mapId];
    const urlSeed = params.get('seed');
    this.seedCode = urlSeed ? normalizeSeedCode(urlSeed) : dailySeedCode(new Date());
    this.isDaily = this.seedCode === dailySeedCode(new Date());
    const beat = Number(params.get('beat'));
    this.beat = Number.isFinite(beat) && beat > 0 ? Math.floor(beat) : null;
    // An invite link: join that room once the world is up (the URL gets rewritten on load).
    const room = normalizeRoomCode(params.get('room') ?? '');
    this.joinCode = room.length === 6 ? room : null;

    this.input.onAction = () => this.action();
    this.input.onPause = () => {
      if (this.ui.settingsOpen) this.ui.closeSettings();
      // A shared run can't be paused.
      else if ((this.state === 'playing' || this.state === 'paused') && !this.mp?.connected) this.togglePause();
      else if (this.state === 'crashed') this.toMenu();
    };
    this.input.onNav = (dir) => {
      if (this.state === 'idle' && !this.ui.settingsOpen) {
        this.audio.play({ type: 'uiMove' });
        void this.switchMap(dir);
      }
    };

    // iOS: motion permission + audio unlock must come from a click/touchend.
    this.surface.addEventListener('click', () => this.gestureUnlock());
    window.addEventListener('keydown', () => void this.audio.unlock(), { once: false });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        if (this.state === 'playing' && !this.autopilot && !this.mp) this.togglePause(true);
        this.audio.suspend();
      } else this.audio.resume();
    });
    new ResizeObserver(() => this.resize()).observe(container);
    if (matchMedia('(pointer: coarse)').matches) document.body.classList.add('touch');
    if (new URLSearchParams(location.search).has('debug')) {
      this.debugEl = document.createElement('div');
      this.debugEl.style.cssText = 'position:fixed;left:8px;bottom:8px;z-index:9;font:11px/1.35 ui-monospace,monospace;color:#fff;background:rgba(0,0,0,.45);padding:6px 8px;border-radius:6px;pointer-events:none;white-space:pre';
      document.body.appendChild(this.debugEl);
    }
  }

  private updateDebug(dt: number): void {
    if (!this.debugEl) return;
    const d = this.debugAcc;
    d.t += dt;
    d.frames++;
    d.worst = Math.max(d.worst, dt);
    if (d.t < 0.5) return;
    const info = this.renderer.gl.info;
    this.debugEl.textContent =
      `${(d.frames / d.t).toFixed(0)} fps  worst ${(d.worst * 1000).toFixed(1)} ms\n` +
      `calls ${info.render.calls}  tris ${(info.render.triangles / 1000).toFixed(0)}k\n` +
      `scale ${this.renderer.scale.toFixed(2)}  ${this.quality.level}  geo ${info.memory.geometries} tex ${info.memory.textures}\n` +
      `x ${this.sim.x.toFixed(0)} z ${this.sim.z.toFixed(0)}  v ${this.sim.speed.toFixed(1)}  storm ${(this.sim.progress - this.sim.stormP).toFixed(0)}\n` +
      `${this.world?.stats ?? ''}`;
    d.t = 0;
    d.frames = 0;
    d.worst = 0;
  }

  async boot(): Promise<void> {
    this.ui.setTheme(this.map.ui, this.map.id);
    this.ui.veilOn(true, GAME_NAME);
    this.resize();
    await this.loadMap(this.map);
    this.state = 'idle';
    this.showMenu();
    this.ui.veilOn(false);
    this.last = performance.now();
    this.renderer.settle(2.5);
    requestAnimationFrame(this.frame);
    if (new URLSearchParams(location.search).has('autostart')) setTimeout(() => this.start(), 600);
    if (this.joinCode) this.openRoom(this.joinCode);
  }

  // ------------------------------------------------------------------ world lifecycle

  private newSim(): Sim {
    return new Sim(seedFromCode(this.seedCode, WORLD_VERSION), this.map);
  }

  private async loadMap(map: MapDef): Promise<void> {
    const t0 = performance.now();
    this.player?.dispose();
    this.world?.dispose();
    this.map = map;
    this.sim = this.newSim();
    this.renderer.setMap(map);
    this.shared.applyMap(map);
    const t1 = performance.now();
    this.world = new World(map, this.sim, this.shared, this.quality, this.textures);
    const t2 = performance.now();
    this.player = new PlayerView(this.world.scene, map, this.world.trail);
    this.player.rig.tint(this.settings.pearl, true);
    this.ghosts?.dispose();
    this.ghosts = new GhostTrails(this.world.scene, this.shared);
    this.ghosts.setColor(this.settings.pearl);
    this.player.setWind(this.shared.uWind.value.x, this.shared.uWind.value.y);
    await this.env.capture(this.shared, map);
    this.world.setEnvironment(this.env.envMap);
    this.player.setEnvMap(this.env.envMap);
    this.player.sync(this.sim, 1, 0, 0, this.world.bursts);
    this.rebuildRemote();
    this.cam.snap();
    this.audio.setTheme(map.audioTheme);
    this.ui.setTheme(map.ui, map.id);
    storage.setLastMap(map.id);
    this.best = storage.best(map.id, this.seedCode);
    this.syncUrl();
    // Stream the start area and pre-compile every shader before revealing anything.
    const start = performance.now();
    while (!this.world.readyAround(this.sim.x, this.sim.z) && performance.now() - start < 8000) {
      this.stepVisuals(1 / 60, 1);
      await new Promise((r) => setTimeout(r, 16));
    }
    this.stepVisuals(1 / 60, 1);
    const t3 = performance.now();
    // Compile everything now, behind the veil: every prop pool and the pearl's live reflection
    // (with its prefilter shaders) included, so nothing compiles mid-run.
    this.world.warmup(true, this.renderer.gl, this.player.rig.center(_probe), this.player.rig.root);
    if (this.world.probe?.ready) this.player.setEnvMap(this.world.probe.texture);
    await this.renderer.warm(this.world.scene, this.cam.camera);
    this.world.warmup(false);
    this.renderOnce();
    this.renderer.settle(2);
    this.loadTimings = { renderer: t1 - t0, world: t2 - t1, stream: t3 - t2, compile: performance.now() - t3 };
    if (this.debugEl) console.info('[cloudpearl] load ms', this.loadTimings);
  }

  private async switchMap(dir: -1 | 1): Promise<void> {
    const i = MAP_ORDER.indexOf(this.map.id);
    await this.changeMap(MAP_ORDER[(i + dir + MAP_ORDER.length) % MAP_ORDER.length]!, false);
  }

  /** Load another map (from the picker, or because the friend switched: `fromNet`). */
  private async changeMap(id: MapId, fromNet: boolean): Promise<void> {
    if (this.state !== 'idle' || id === this.map.id) return;
    this.state = 'busy';
    this.mp?.setPhase('loading');
    const next = MAPS[id];
    this.ui.veilOn(true, next.name);
    await new Promise((r) => setTimeout(r, 380));
    await this.loadMap(next);
    this.state = 'idle';
    this.showMenu();
    this.ui.veilOn(false);
    this.afterWorldChange(fromNet);
  }

  /** Tell the friend (unless they caused it), announce we're ready, and catch up on their changes. */
  private afterWorldChange(fromNet: boolean): void {
    if (!this.mp) return;
    if (!fromNet) this.mp.pushConfig();
    this.mp.setPhase('menu');
    const p = this.pendingAdopt;
    if (p) {
      this.pendingAdopt = null;
      this.adopt(p.map, p.seed);
    }
  }

  private async setSeed(code: string, daily: boolean, fromNet = false): Promise<void> {
    if (this.state !== 'idle' && this.state !== 'crashed') return;
    const norm = normalizeSeedCode(code);
    if (!norm || (norm === this.seedCode && this.state === 'idle')) return;
    this.state = 'busy';
    this.mp?.setPhase('loading');
    this.seedCode = norm;
    this.isDaily = daily || norm === dailySeedCode(new Date());
    this.beat = null;
    // Cover everything before touching the world: the old terrain is torn down and the new one
    // streams in behind the veil, then the scene settles for a few frames before it's revealed.
    this.ui.closeSheets();
    this.ui.hideAll();
    this.ui.veilOn(true, this.isDaily ? 'Daily map' : 'New world');
    await new Promise((r) => setTimeout(r, 380));
    this.ghosts?.clear();
    this.sim = this.newSim();
    this.world!.setSim(this.sim);
    this.player!.reset();
    this.world!.trail.clear();
    this.rebuildRemote();
    this.best = storage.best(this.map.id, this.seedCode);
    this.syncUrl();
    const start = performance.now();
    while (!this.world!.readyAround(this.sim.x, this.sim.z) && performance.now() - start < 6000) {
      this.stepVisuals(1 / 60, 1);
      await new Promise((r) => setTimeout(r, 16));
    }
    this.cam.snap();
    for (let i = 0; i < 6; i++) {
      this.stepVisuals(1 / 60, 1);
      this.renderOnce();
      await new Promise((r) => setTimeout(r, 16));
    }
    this.state = 'idle';
    this.showMenu();
    this.ui.veilOn(false);
    this.afterWorldChange(fromNet);
  }

  private async applySettings(s: SettingsState): Promise<void> {
    const qualityChanged = s.quality !== this.settings.quality;
    if (s.pearl !== this.settings.pearl) {
      this.player?.rig.tint(s.pearl);
      this.ghosts?.setColor(s.pearl);
      this.mp?.setLook(s.pearl);
    }
    this.settings = s;
    storage.saveSettings(s);
    this.audio.setMuted(!s.sound);
    this.audio.setVolumes({ music: s.music ? 0.45 : 0 });
    if (qualityChanged && (this.state === 'idle' || this.state === 'crashed')) {
      this.state = 'busy';
      this.ui.closeSettings();
      this.ui.veilOn(true, 'Repainting…');
      this.quality = presetFor(s.quality);
      this.renderer.setQuality(this.quality);
      this.resize();
      await this.loadMap(this.map);
      this.state = 'idle';
      this.showMenu();
      this.ui.veilOn(false);
    }
    if (this.state === 'idle') this.showMenu();
  }

  // ------------------------------------------------------------------ flow

  private gestureUnlock(): void {
    void this.audio.unlock();
    const DOE = window.DeviceOrientationEvent as unknown as { requestPermission?: unknown } | undefined;
    const needsPrompt = !!DOE && typeof DOE.requestPermission === 'function';
    if (needsPrompt && !this.tiltAsked && this.input.isTouch) {
      this.tiltAsked = true;
      void this.input.requestTilt().then(() => {
        if (this.pendingStart) {
          this.pendingStart = false;
          this.start();
        }
      });
    } else if (this.pendingStart) {
      this.pendingStart = false;
      this.start();
    }
  }

  /** Tap / Space / click: start, retry or resume depending on state. */
  private action(): void {
    void this.audio.unlock();
    if (this.ui.settingsOpen) return;
    switch (this.state) {
      case 'idle': {
        // In a room with a friend, Run asks for a shared start (both count down together).
        if (this.mp?.connected) {
          if (!this.mp.requestGo()) this.ui.toast('Waiting for your friend');
          break;
        }
        const DOE = window.DeviceOrientationEvent as unknown as { requestPermission?: unknown } | undefined;
        const needsPrompt = !!DOE && typeof DOE.requestPermission === 'function' && !this.tiltAsked && this.input.isTouch;
        if (needsPrompt) this.pendingStart = true;
        else this.start();
        break;
      }
      case 'crashed':
        if (this.mp?.connected) {
          // Rematch from the results card.
          if (this.resultShown && this.ui.crashReady && !this.mp.requestGo()) this.ui.toast('Waiting for your friend');
        } else if (this.crashShown && this.ui.crashReady) this.retry();
        break;
      case 'paused':
        this.togglePause(false);
        break;
    }
  }

  private start(): void {
    if (this.state !== 'idle') return;
    this.input.calibrate();
    this.input.enabled = true;
    this.recorder.clear();
    this.sim.start();
    this.acc = 0;
    this.passedBest = false;
    this.state = 'playing';
    this.mp?.setPhase('running');
    // First few runs: a brief line of controls.
    const hint =
      storage.runs() >= 3
        ? null
        : !this.input.isTouch
          ? 'Hold Space to dive · let go to fly · A / D to steer'
          : this.input.usingTilt
            ? 'Hold to dive · let go to fly · lean to steer'
            : 'Hold to dive · let go to fly · drag to steer';
    this.ui.showHud(this.input.isTouch, hint);
    this.audio.play({ type: 'start' });
    storage.addRun();
  }

  private retry(): void {
    if (this.state !== 'crashed' && this.state !== 'paused') return;
    this.state = 'busy';
    this.fadeTarget = 0;
    this.ui.hideAll();
    setTimeout(() => {
      this.resetRun();
      const t0 = performance.now();
      const wait = (): void => {
        if (this.world!.readyAround(this.sim.x, this.sim.z) || performance.now() - t0 > 900) {
          this.fadeTarget = 1;
          this.state = 'idle';
          this.start();
        } else requestAnimationFrame(wait);
      };
      wait();
    }, 220);
  }

  private resetRun(): void {
    // The run that just ended joins the ghost trails (solo only).
    if (this.sim.started && !this.mp?.connected) this.ghosts?.commit();
    else this.ghosts?.discard();
    this.sim.reset();
    this.player!.reset();
    this.world?.trail.clear();
    this.spectating = false;
    this.reviveAt = 0;
    this.resultShown = false;
    this.bumpCool = 0;
    this.ui.countdown(null);
    for (const t of this.countTimers) clearTimeout(t);
    this.countTimers = [];
    // Shared runs start side by side.
    if (this.mp?.connected) {
      const side = this.mp.isHost ? -1 : 1;
      this.sim.placeAt(side * SPAWN_X, 0);
      this.remote?.park(-side * SPAWN_X, 0);
      this.remoteView?.reset();
    }
    this.timeScale = 1;
    this.crashT = 0;
    this.crashShown = false;
    this.post.storm = 0;
    this.post.desat = 0;
    this.input.enabled = false;
    this.cam.snap();
  }

  private toMenu(): void {
    if (this.state === 'busy' || this.state === 'boot') return;
    this.state = 'busy';
    this.fadeTarget = 0;
    this.ui.hideAll();
    setTimeout(() => {
      this.resetRun();
      this.state = 'idle';
      this.showMenu();
      this.fadeTarget = 1;
      if (this.mp) this.afterWorldChange(true);
    }, 260);
  }

  private togglePause(force?: boolean): void {
    const pause = force ?? this.state === 'playing';
    if (pause && this.state === 'playing') {
      this.state = 'paused';
      this.ui.showPause(true);
      this.input.enabled = false;
    } else if (!pause && this.state === 'paused') {
      this.state = 'playing';
      this.ui.showPause(false);
      this.input.enabled = true;
      this.acc = 0;
      this.last = performance.now();
    }
  }

  private showMenu(): void {
    this.best = storage.best(this.map.id, this.seedCode);
    this.ui.showMenu({
      mapName: this.map.name,
      room: this.mp?.info() ?? null,
      isDaily: this.isDaily,
      best: this.best,
      beat: this.beat,
      isTouch: this.input.isTouch || matchMedia('(pointer: coarse)').matches,
    });
  }

  // ------------------------------------------------------------------ events & death

  private handleEvents(events: SimEvent[]): void {
    const world = this.world!;
    const player = this.player!;
    for (const e of events) {
      switch (e.type) {
        case 'launch':
          this.audio.play({ type: 'launch', speed: e.speed });
          break;
        case 'dive':
          this.audio.play({ type: 'dive' });
          break;
        case 'land': {
          player.land(e.quality, e.impact, this.time, world.bursts);
          this.audio.play({ type: 'land', quality: e.quality, combo: e.combo, impact: e.impact });
          if (e.quality === 'perfect') {
            this.ui.callout('Perfect', 'perfect', e.combo > 1 ? `×${e.combo}` : undefined);
            this.post.flash = Math.max(this.post.flash, 0.06);
            this.post.flashColor.set('#fff4d0');
          } else if (e.quality === 'rough') {
            this.cam.shake(0.25 + e.impact * 0.5);
          }
          this.cam.shake(e.impact * 0.25);
          break;
        }
        case 'closeCall':
          player.closeCall(e.side, this.time, world.bursts);
          this.audio.play({ type: 'closeCall', side: e.side });
          this.ui.callout('Close call', 'close');
          this.cam.shake(0.18);
          break;
        case 'milestone':
          // The distance counter already says it; a chime is enough.
          this.audio.play({ type: 'milestone', meters: e.meters });
          break;
        case 'gate':
          this.gateCombo = this.time - this.lastGateT < 12 ? this.gateCombo + 1 : 1;
          this.lastGateT = this.time;
          player.gate(this.time, world.bursts);
          this.audio.play({ type: 'gate', combo: this.gateCombo });
          this.ui.callout('Wind gate', 'gate', this.gateCombo > 1 ? `×${this.gateCombo}` : undefined);
          this.post.flash = Math.max(this.post.flash, 0.1);
          this.post.flashColor.set('#d8f0ff');
          this.cam.kick(0.6);
          break;
        case 'crash':
        case 'fall':
        case 'caught':
          this.onDeath();
          break;
      }
    }
    if (!this.passedBest && this.best && this.best > 50 && this.sim.distance > this.best) {
      this.passedBest = true;
      this.ui.callout('New best', 'best');
      this.audio.play({ type: 'newBest' });
    }
  }

  private onDeath(): void {
    const sim = this.sim;
    const player = this.player!;
    const world = this.world!;
    this.state = 'crashed';
    this.input.enabled = false;
    this.crashT = 0;
    this.crashShown = false;
    if (sim.cause === 'obstacle' && sim.deathObstacle) {
      const mat = HAZARDS[sim.deathObstacle.kind].material;
      player.crash(sim, mat, this.time, world.bursts);
      this.audio.play({ type: 'crash', material: mat });
      this.timeScale = this.mp?.connected ? 1 : 0.22;
      this.cam.shake(0.6);
      this.post.flash = 0.45;
      this.post.flashColor.set('#ffffff');
    } else if (sim.cause === 'caught') {
      player.caught();
      this.audio.play({ type: 'caught' });
      this.cam.shake(0.5);
    } else {
      this.audio.play({ type: 'fall' });
    }
    this.cam.startCrash(player.position);

    if (this.mp?.connected) {
      this.onSharedDeath();
      return;
    }
    const dist = sim.distance;
    const newBest = !this.best || dist > this.best;
    if (newBest && dist > 0) {
      storage.setBest(this.map.id, this.seedCode, dist);
      storage.setReplay(this.map.id, this.seedCode, this.recorder.encode());
    }
  }

  private showCrashCard(): void {
    const sim = this.sim;
    const dist = sim.distance;
    const prevBest = this.best ?? 0;
    const newBest = dist > prevBest && dist > 0;
    let cause: string;
    if (sim.cause === 'obstacle' && sim.deathObstacle) cause = `Taken out by a ${HAZARDS[sim.deathObstacle.kind].label}`;
    else if (sim.cause === 'caught') cause = `${capitalise(this.map.chaser.verb)}`;
    else cause = capitalise(this.map.fallVerb);
    this.ui.showCrash({
      distance: dist,
      best: Math.max(prevBest, dist),
      newBest,
      cause,
      perfects: sim.perfects,
      closeCalls: sim.closeCalls,
      topSpeedKmh: Math.round(sim.maxSpeed * 3.6),
      beat: this.beat,
    });
    if (newBest) {
      this.best = dist;
      this.audio.play({ type: 'newBest' });
    }
    this.crashShown = true;
  }

  // ------------------------------------------------------------------ frame

  private frame = (now: number): void => {
    requestAnimationFrame(this.frame);
    // 60 fps cap on high-refresh displays (ProMotion, 144 Hz…): skip a vsync unless a 60 Hz
    // frame's worth of time (less half a vsync of slack) has passed since the last rendered one.
    // Pacing stays even, and a slow frame never costs an extra vsync. Halves GPU work (and
    // heat/fan noise) on a 120 Hz MacBook. Displays at ~100 Hz or less are left alone.
    const raw = now - this.rafPrev;
    this.rafPrev = now;
    if (raw > 2 && raw < 40) this.refreshMs += (raw - this.refreshMs) * (raw < this.refreshMs ? 0.25 : 0.02);
    if (this.settings.cap60 && this.refreshMs < 1000 / 100 && now - this.last < 1000 / 60 - this.refreshMs * 0.5) return;
    // Paused, nothing in the world moves: redraw at a trickle (a paused game left open in a tab
    // was still rendering flat out).
    if (this.state === 'paused' && now - this.last < 50) return;
    const dtReal = Math.min((now - this.last) / 1000, 0.1);
    this.last = now;
    if (dtReal <= 0) return;
    // (Paused frames are throttled on purpose — they say nothing about the GPU's headroom.)
    if (this.state !== 'paused') this.renderer.adapt(dtReal);
    this.input.update(dtReal);

    let alpha = 1;
    if (this.state === 'playing') {
      this.acc += dtReal;
      let steps = 0;
      while (this.acc >= SIM_DT && steps < 12) {
        const inp = this.autopilot ? autopilot(this.sim, this.autoInput) : { hold: this.input.hold, steer: this.input.steerQ };
        this.recorder.push(inp);
        this.sim.step(inp);
        this.acc -= SIM_DT;
        steps++;
        if (this.mp) this.collidePearls(performance.now());
        if (this.sim.events.length) this.handleEvents(this.sim.events);
        if (this.state !== 'playing') break;
      }
      if (steps >= 12) this.acc = 0;
      alpha = this.state === 'playing' ? this.acc / SIM_DT : 1;
      if (!this.mp?.connected && this.sim.alive) this.ghosts?.record(this.sim.x, this.sim.y + PEARL_RADIUS, this.sim.z);
    } else if (this.state === 'crashed') {
      this.timeScale += (1 - this.timeScale) * Math.min(1, dtReal * 1.4);
      this.crashT += dtReal;
      if (this.sim.cause === 'fall') {
        this.acc += dtReal * this.timeScale;
        while (this.acc >= SIM_DT) {
          this.sim.step({ hold: false, steer: 0 });
          this.acc -= SIM_DT;
        }
        alpha = this.acc / SIM_DT;
      }
      if (this.mp?.connected && !this.crashShown) this.updateShared();
      else {
        const delay = this.sim.cause === 'fall' ? 1.25 : this.sim.cause === 'caught' ? 1.1 : 0.85;
        if (!this.crashShown && !this.resultShown && this.crashT > delay) this.showCrashCard();
      }
    }
    if (this.mp) this.netFrame(performance.now(), dtReal);

    const dt = dtReal * (this.state === 'crashed' ? this.timeScale : 1);
    this.realDt = dtReal;
    this.stepVisuals(dt, alpha);
    this.updateProbe();
    this.renderer.render(this.world!.scene, this.cam.camera, this.post, this.time, dt);
    this.updateDebug(dtReal);
  };

  /** Advance everything visual by dt and update post params (no rendering). */
  private stepVisuals(dt: number, alpha: number): void {
    const world = this.world;
    const player = this.player;
    if (!world || !player) return;
    if (this.state !== 'paused') this.time += dt;
    this.shared.uTime.value = this.time;
    const sim = this.sim;
    player.sync(sim, alpha, this.state === 'paused' ? 0 : dt, this.time, world.bursts);
    if (this.remote && this.remoteView) this.remoteView.sync(this.remote, 1, this.state === 'paused' ? 0 : dt, this.time, world.bursts);
    // Out of a race: the camera follows the friend.
    const watch = this.spectating && this.remoteView && this.remote ? this.remoteView : player;
    const watchState = watch === player ? sim : this.remote!;

    const p = watch.position;
    this.shared.uPlayerPos.value.copy(p);
    this.shared.uPlayerVel.value.set(player.velocity.x, player.velocity.y, player.velocity.z, Math.min(sim.speed / 60, 1));
    const w = sim.world;
    const fw = watch.forward;
    const groundAhead = w.height(p.x + fw.x * 40, p.z + fw.z * 40);
    const behindX = p.x - fw.x * 7;
    const behindZ = p.z - fw.z * 7;
    const groundBehind = w.mask(behindX, behindZ) > 0.5 ? Math.max(w.height(behindX, behindZ), w.height(p.x - fw.x * 4, p.z - fw.z * 4)) : -1e4;
    const mode = this.state === 'idle' || this.state === 'boot' || (this.state === 'busy' && !sim.started) ? 'menu' : this.state === 'crashed' && watch === player ? 'crash' : 'play';
    // On the menu with a friend, frame the pair of you.
    const pair = mode === 'menu' && this.remoteView?.rig.root.visible;
    this.cam.update(
      {
        target: pair ? _mid.copy(p).add(this.remoteView!.position).multiplyScalar(0.5) : p,
        forward: watch.forward,
        right: watch.right,
        speed: watchState.started && watchState.alive ? watchState.speed : 0,
        steer: watch === player && sim.started && sim.alive ? sim.steer : 0,
        airHeight: watchState.grounded ? 0 : watch.airHeight,
        groundAhead,
        groundBehind,
        mode,
        time: this.time,
        realDt: this.realDt,
      },
      Math.max(dt, 1e-4),
    );
    world.menuPearl = mode === 'menu' ? player.position : null;
    world.update(this.cam.camera, p, watch.forward, dt);
    this.updateFriendBeam();
    if (this.ghosts) this.ghosts.group.visible = !this.mp?.connected;

    // Post FX targets.
    const playing = this.state === 'playing' || (this.state === 'paused' && sim.started);
    const speedTarget = this.settings.speedFx && playing ? smoothstep(24, 86, sim.speed) : 0;
    this.speedFx += (speedTarget - this.speedFx) * Math.min(1, dt * 4);
    this.post.speed = this.speedFx;
    _ahead.copy(p).addScaledVector(player.forward, 45).add(_proj.set(0, 1.2, 0));
    _ahead.project(this.cam.camera);
    const fx = Math.min(0.8, Math.max(0.2, _ahead.x * 0.5 + 0.5));
    const fy = Math.min(0.8, Math.max(0.25, _ahead.y * 0.5 + 0.5));
    this.post.focus.x += (fx - this.post.focus.x) * Math.min(1, dt * 6);
    this.post.focus.y += (fy - this.post.focus.y) * Math.min(1, dt * 6);
    _proj.copy(p).add(_ahead.set(0, 0.6, 0)).project(this.cam.camera);
    const heroDist = this.cam.camera.position.distanceTo(p);
    this.post.hero.set(_proj.x * 0.5 + 0.5, _proj.y * 0.5 + 0.5, Math.min(0.2, 1.3 / Math.max(heroDist, 1) / Math.tan((this.cam.camera.fov * Math.PI) / 360)) * 0.5);
    const stormTarget = playing ? Math.pow(sim.chaserProximity, 1.6) : this.state === 'crashed' && sim.cause === 'caught' ? Math.min(1, this.crashT * 1.2) : 0;
    this.post.storm += (stormTarget - this.post.storm) * Math.min(1, dt * 3);
    this.post.flash *= Math.exp(-dt * 7);
    const desatTarget = this.state === 'crashed' ? 0.35 : 0;
    this.post.desat += (desatTarget - this.post.desat) * Math.min(1, dt * 3);
    this.post.fade += (this.fadeTarget - this.post.fade) * Math.min(1, dt * 7);

    this.audio.update(
      {
        state: this.state === 'playing' ? 'playing' : this.state === 'paused' ? 'paused' : this.state === 'crashed' ? 'crashed' : 'idle',
        speed: sim.started ? sim.speed : 0,
        speedNorm: sim.started ? Math.min(1, Math.max(0, (sim.speed - 12) / 70)) : 0,
        grounded: sim.grounded,
        holding: sim.holding && playing,
        steer: sim.steer,
        chaserProximity: playing ? sim.chaserProximity : 0,
        flow: sim.flow,
      },
      dt,
    );

    if (this.state === 'playing') {
      this.ui.setDistance(sim.distance, this.best);
      const cf = this.cam.camera.getWorldDirection(_proj);
      const l = Math.hypot(cf.x, cf.z) || 1;
      // Angle from the camera's heading to north (−z): positive = north is to the right.
      this.ui.setNorth(Math.atan2(-cf.x / l, -cf.z / l));
    }
  }

  private renderOnce(): void {
    this.updateProbe();
    this.renderer.render(this.world!.scene, this.cam.camera, this.post, this.time, 1 / 60);
  }

  /** Keep the pearl's live reflection probe fresh (one cube face per frame). */
  private updateProbe(): void {
    const world = this.world;
    const player = this.player;
    if (!world || !player || !world.probe) return;
    world.renderProbe(this.renderer.gl, player.rig.center(_probe), player.rig.root);
    if (world.probe.ready) player.setEnvMap(world.probe.texture);
  }

  private resize(): void {
    const w = this.container.clientWidth || window.innerWidth;
    const h = this.container.clientHeight || window.innerHeight;
    this.cam.setAspect(w / h);
    this.renderer.resize();
  }

  /** Dev helper (console / ?autopilot): teleport the run `progress` metres north. */
  debugJump(progress: number, speed = 40): void {
    const sim = this.sim;
    const w = sim.world;
    let x = 0;
    let z = -progress;
    if (w.islands) {
      let best = Infinity;
      for (const isl of w.islandsNear(x, z, [], 200)) {
        const d = Math.hypot(isl.cx - x, isl.cz - z);
        if (d < best) {
          best = d;
          x = isl.cx;
          z = isl.cz + isl.r * 0.4;
        }
      }
    }
    sim.x = sim.prevX = x;
    sim.z = sim.prevZ = z;
    sim.y = sim.prevY = w.height(x, z);
    sim.vx = 0;
    sim.vz = -speed;
    sim.vy = 0;
    sim.grounded = true;
    sim.bestProgress = Math.max(sim.bestProgress, -z);
    sim.stormP = -z - 200;
    this.player?.reset();
    this.cam.snap();
  }

  // ------------------------------------------------------------------ sharing

  /** The URL always describes this world (map + seed, plus the room while you're in one). */
  private shareUrl(withBeat: boolean): string {
    const u = new URL(location.href);
    u.search = '';
    u.searchParams.set('map', this.map.id);
    u.searchParams.set('seed', this.seedCode);
    if (this.mp && !withBeat) u.searchParams.set('room', formatRoomCode(this.mp.code));
    if (withBeat && this.sim.distance > 0) u.searchParams.set('beat', String(this.sim.distance));
    return u.toString();
  }

  private syncUrl(): void {
    try {
      const u = new URL(location.href);
      u.searchParams.set('map', this.map.id);
      u.searchParams.set('seed', this.seedCode);
      if (this.beat) u.searchParams.set('beat', String(this.beat));
      else u.searchParams.delete('beat');
      if (this.mp) u.searchParams.set('room', formatRoomCode(this.mp.code));
      else u.searchParams.delete('room');
      history.replaceState(null, '', u.toString());
    } catch {
      /* ignore */
    }
  }

  private async copyLink(kind: 'world' | 'challenge'): Promise<void> {
    const url = this.shareUrl(kind === 'challenge');
    try {
      await navigator.clipboard.writeText(url);
      this.ui.toast(kind === 'challenge' ? 'Challenge link copied' : this.mp ? 'Invite link copied' : 'Link copied');
    } catch {
      this.ui.toast(url);
    }
  }

  // ------------------------------------------------------------------ shared runs

  /** Host a room (no code) or join one. */
  private openRoom(code?: string): void {
    if (this.mp) return;
    this.mp = new Session(
      {
        config: () => ({ map: this.map.id, seed: this.seedCode, color: this.settings.pearl }),
        look: (c) => this.setFriendColor(c),
        adopt: (map, seed) => this.adopt(map, seed),
        go: () => this.sharedGo(),
        state: (st) => this.remote?.receive(st, performance.now()),
        bumped: (vx, vy, vz, px, pz) => this.onBumped(vx, vy, vz, px, pz),
        changed: () => this.onRoomChanged(),
        left: (reason, closed) => this.onFriendGone(reason, closed),
      },
      code,
    );
    this.syncUrl();
    this.ui.openRoomSheet(true);
    this.onRoomChanged();
  }

  private leaveRoom(reason: string): void {
    this.mp?.leave();
    this.onFriendGone(reason, true);
  }

  private setMode(mode: RunMode): void {
    this.mp?.setMode(mode);
  }

  private onRoomChanged(): void {
    const mp = this.mp;
    if (!mp) return;
    const connected = mp.connected;
    if (connected && !this.remote) {
      this.rebuildRemote();
      this.ui.openRoomSheet(false);
      this.ui.toast(mp.isHost ? 'Your friend joined' : `Joined room ${formatRoomCode(mp.code)}`);
      if (this.state === 'idle') mp.setPhase('menu');
    }
    if (!connected && this.remote) this.dropRemote();
    this.syncUrl();
    this.ui.setRoom(mp.info());
  }

  private onFriendGone(reason: string, closed: boolean): void {
    this.ui.toast(reason);
    this.dropRemote();
    if (closed) {
      this.mp = null;
      this.pendingAdopt = null;
      this.ui.openRoomSheet(false);
      this.syncUrl();
    }
    this.ui.setRoom(this.mp?.info() ?? null);
    this.ui.note(null);
    // Back to a solo start position on the menu.
    if (this.state === 'idle' && !this.sim.started) {
      this.sim.placeAt(0, 0);
      this.player?.reset();
    }
    // Out and waiting on a friend who's gone: show the solo card instead.
    if (this.state === 'crashed' && !this.crashShown && !this.resultShown) this.crashT = Math.max(this.crashT, 2);
  }

  /** The friend changed world (or we just joined theirs). */
  private adopt(map: MapId, seed: string): void {
    if (map === this.map.id && seed === this.seedCode) return;
    if (this.state !== 'idle') {
      this.pendingAdopt = { map, seed };
      return;
    }
    this.beat = null;
    if (map !== this.map.id) {
      this.seedCode = seed;
      this.isDaily = seed === dailySeedCode(new Date());
      void this.changeMap(map, true);
    } else void this.setSeed(seed, false, true);
  }

  /** The friend's pearl, rebuilt for the current world (or removed when there's no friend). */
  private rebuildRemote(): void {
    this.dropRemote();
    const mp = this.mp;
    if (!mp?.connected || !this.world) return;
    this.remote = new RemotePearl(this.sim.world);
    this.remoteView = new PlayerView(this.world.scene, this.map, this.world.trail, this.friendColor);
    this.friendBeam = new Mesh(beamGeometry(), createBeamMaterial(this.shared, this.friendColor));
    this.friendBeam.frustumCulled = false;
    this.friendBeam.renderOrder = 5;
    this.friendBeam.visible = false;
    this.world.scene.add(this.friendBeam);
    this.remoteView.setWind(this.shared.uWind.value.x, this.shared.uWind.value.y);
    this.remoteView.setEnvMap(this.world.probe?.ready ? this.world.probe.texture : this.env.envMap);
    const side = mp.isHost ? -1 : 1;
    this.remote.park(-side * SPAWN_X, 0);
    if (!this.sim.started) {
      this.sim.placeAt(side * SPAWN_X, 0);
      this.player?.reset();
    }
    this.remoteWasAlive = true;
    this.remoteWasGrounded = true;
  }

  private dropRemote(): void {
    this.remoteView?.dispose();
    this.remoteView = null;
    if (this.friendBeam) {
      this.friendBeam.removeFromParent();
      this.friendBeam.geometry.dispose();
      (this.friendBeam.material as ShaderMaterial).dispose();
      this.friendBeam = null;
    }
    this.remote = null;
    this.spectating = false;
  }

  private setFriendColor(c: string): void {
    if (!/^#[0-9a-f]{6}$/i.test(c)) return;
    this.friendColor = c;
    this.remoteView?.rig.tint(c);
    if (this.friendBeam) ((this.friendBeam.material as ShaderMaterial).uniforms['uBeam']!.value as Color).set(c);
  }

  /**
   * The friend's beam: a tall shaft of their colour over their pearl while you're both running,
   * fading out when they're close (you can see them) and thickening with distance so it stays
   * a readable line on the horizon.
   */
  private updateFriendBeam(): void {
    const beam = this.friendBeam;
    const view = this.remoteView;
    const r = this.remote;
    if (!beam || !view || !r) return;
    const on = view.rig.root.visible && r.started && r.alive && (this.state === 'playing' || this.state === 'crashed');
    beam.visible = on;
    if (!on) return;
    const d = this.cam.camera.position.distanceTo(view.position);
    beam.position.set(view.position.x, view.position.y + PEARL_RADIUS * 2.2, view.position.z);
    const w = Math.max(0.35, d * 0.0035);
    beam.scale.set(w, 900, w);
    (beam.material as ShaderMaterial).uniforms['uOpacity']!.value = smoothstep(14, 60, d);
  }

  /** Both ready: 3 · 2 · 1 · Go, then a side-by-side start (also the rematch from results). */
  private sharedGo(): void {
    if (this.state !== 'idle' && this.state !== 'crashed') return;
    const fromResults = this.state === 'crashed';
    this.state = 'busy';
    this.ui.closeSheets();
    this.ui.hideAll();
    this.mp?.setPhase('countdown');
    if (fromResults) this.fadeTarget = 0;
    const t0 = fromResults ? 260 : 0;
    window.setTimeout(() => {
      this.resetRun();
      this.fadeTarget = 1;
      [3, 2, 1, 0].forEach((n, i) => {
        this.countTimers.push(
          window.setTimeout(() => {
            this.ui.countdown(n);
            this.audio.play({ type: n > 0 ? 'uiMove' : 'uiSelect' });
            if (n === 0) {
              this.state = 'idle';
              this.start();
              this.countTimers.push(window.setTimeout(() => this.ui.countdown(null), 650));
            }
          }, 250 + i * 800),
        );
      });
    }, t0);
  }

  /** Our pearl as the friend should see it. */
  private localState(): NetState {
    const s = this.sim;
    const r = (v: number): number => Math.round(v * 1000) / 1000;
    return { k: 's', x: r(s.x), y: r(s.y), z: r(s.z), vx: r(s.vx), vy: r(s.vy), vz: r(s.vz), g: s.grounded ? 1 : 0, h: s.holding ? 1 : 0, a: s.alive ? 1 : 0, r: s.started ? 1 : 0, d: s.distance, s: r(s.stormP), f: s.flow, c: s.cause };
  }

  /**
   * Pearl-on-pearl collision, per sim tick, against the friend's dead-reckoned position. Equal
   * masses, restitution 0.6: whoever sees the contact first applies its half and sends the other
   * half; a short cooldown stops the same contact being counted twice.
   */
  private collidePearls(now: number): void {
    this.bumpCool -= SIM_DT;
    const mp = this.mp;
    const remote = this.remote;
    const sim = this.sim;
    if (!mp?.connected || !remote?.live || !remote.started || !remote.alive || !sim.alive || this.bumpCool > 0) return;
    const q = remote.predict(now, mp.latency);
    if (!q) return;
    const reach = PEARL_RADIUS * 2.1;
    const dx = sim.x - q.x;
    const dy = sim.y - q.y;
    const dz = sim.z - q.z;
    const d2 = dx * dx + dy * dy + dz * dz;
    if (d2 >= reach * reach || d2 < 1e-8) return;
    const d = Math.sqrt(d2);
    const nx = dx / d;
    const ny = dy / d;
    const nz = dz / d;
    const vn = (sim.vx - q.vx) * nx + (sim.vy - q.vy) * ny + (sim.vz - q.vz) * nz;
    if (vn >= 0) return;
    const j = (-(1 + 0.6) * vn) / 2;
    const push = (reach - d) * 0.5;
    sim.bump(nx * j, ny * j, nz * j, nx * push, nz * push);
    mp.bump(-nx * j, -ny * j, -nz * j, -nx * push, -nz * push);
    this.bumpCool = 0.3;
    this.bumpFx(j);
  }

  private onBumped(vx: number, vy: number, vz: number, px: number, pz: number): void {
    if (this.bumpCool > 0 || this.state !== 'playing') return;
    this.sim.bump(vx, vy, vz, px, pz);
    this.bumpCool = 0.3;
    this.bumpFx(Math.hypot(vx, vy, vz));
  }

  private bumpFx(j: number): void {
    const k = Math.min(1, j / 14);
    this.audio.play({ type: 'bump', impact: k });
    this.cam.shake(0.15 + k * 0.35);
    this.player?.rig.impulse(0.3 + k * 0.6);
    if (k > 0.35) this.ui.callout('Bump', 'bump');
  }

  /** Our pearl is out in a shared run: respawn on the friend (co-op), spectate (race), or end it. */
  private onSharedDeath(): void {
    this.mp!.sendState(this.localState(), 0, true);
    this.reviveAt = this.mp!.mode === 'coop' ? REVIVE_DELAY : 0;
  }

  private get friendAlive(): boolean {
    const r = this.remote;
    return !!this.mp?.connected && !!r?.live && r.started && r.alive;
  }

  /** While we're out of a shared run (crashed state). */
  private updateShared(): void {
    const mp = this.mp!;
    if (this.friendAlive) {
      if (mp.mode === 'coop') {
        this.ui.note('Respawning on your friend…');
        // Ideally as they touch down; if they're flying a long way, anywhere above solid ground.
        const r = this.remote!;
        if (this.crashT >= this.reviveAt && (r.grounded || (this.crashT >= this.reviveAt + 1.5 && this.sim.world.mask(r.x, r.z) > 0.6))) this.revive();
      } else {
        this.ui.note('Out — your friend is still running');
        if (this.crashT > 1.4 && !this.spectating) {
          this.spectating = true;
          this.cam.snap();
        }
      }
      return;
    }
    if (!this.resultShown && this.crashT > 1.2) {
      this.resultShown = true;
      this.spectating = false;
      this.ui.note(null);
      const you = this.sim.distance;
      const friend = this.remote?.distance ?? 0;
      this.ui.showResult({ mode: mp.mode, you, friend, team: Math.max(you, friend) });
      if (mp.mode === 'race' ? you > friend : true) this.audio.play({ type: 'newBest' });
      mp.setPhase('results');
    }
  }

  /** Co-op: back in, beside the friend, matching their speed. */
  private revive(): void {
    const r = this.remote!;
    const w = this.sim.world;
    const hs = Math.hypot(r.vx, r.vz) || 1;
    const side = this.mp!.isHost ? -1 : 1;
    let x = r.x + (-r.vz / hs) * side * 3.2;
    let z = r.z + (r.vx / hs) * side * 3.2;
    if (w.mask(x, z) < 0.6) {
      x = r.x - (r.vx / hs) * 3;
      z = r.z - (r.vz / hs) * 3;
    }
    this.sim.respawn(x, z, r.vx, r.vz, r.stormP);
    // A moment of grace so arriving beside them doesn't count as a bump.
    this.bumpCool = 1.2;
    this.player!.reset();
    this.state = 'playing';
    this.input.enabled = true;
    this.timeScale = 1;
    this.crashT = 0;
    this.reviveAt = 0;
    this.spectating = false;
    this.acc = 0;
    this.ui.note(null);
    this.cam.snap();
    this.audio.play({ type: 'start' });
  }

  /** Per frame: stream our pearl, move theirs, play their landings / crashes. */
  private netFrame(now: number, dt: number): void {
    const mp = this.mp!;
    if (mp.connected) mp.sendState(this.localState(), dt);
    const remote = this.remote;
    const view = this.remoteView;
    if (!remote || !view || !this.world) return;
    const vy = remote.vy;
    remote.update(now, mp.latency, dt);
    if (remote.live && remote.started) {
      if (this.remoteWasAlive && !remote.alive) {
        if (remote.cause === 'caught') view.caught();
        else if (remote.cause === 'obstacle') view.crash(remote, 'stone', this.time, this.world.bursts);
      }
      if (!this.remoteWasAlive && remote.alive) view.reset();
      if (!this.remoteWasGrounded && remote.grounded && remote.alive) view.land('good', Math.min(1, Math.abs(vy) / 30), this.time, this.world.bursts);
    }
    this.remoteWasAlive = remote.alive;
    this.remoteWasGrounded = remote.grounded;
    // Only show the friend's pearl when they're in this world with us.
    view.rig.root.visible = mp.friendReady || mp.friendRunning;
    if (this.state === 'playing' || (this.state === 'crashed' && this.spectating)) {
      const d = remote.distance;
      this.ui.setFriend(!remote.started ? '' : remote.alive ? `friend ${d.toLocaleString('en-US')} m` : mp.mode === 'coop' ? 'friend down' : `friend out · ${d.toLocaleString('en-US')} m`);
    }
  }
}

function smoothstep(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
